import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';

import { useUser } from '@/allauth_auth/hooks';
import { QueryErrorState } from '@/lib/components';

import { ErrorBoundary } from '../../../components';
import { formatFallback } from '../../utils/labelFormatters';
import { SectionCard } from '../components/SectionCard';
import { transformToTimelineItems } from './collapseAssociationEvents';
import { collapseCascadeEvents } from './collapseCascadeEvents';
import { collapseTimelineEvents } from './collapseTimelineEvents';
import {
  emptyViewStateLookups,
  ViewStateLookupsProvider,
} from './context/TimelineLookupsContext';
import { TimelineEventList } from './TimelineEventList';
import { ALL_EVENT_TYPES } from './timelineFilterDefaults';
import { TimelineFilters } from './TimelineFilters';
import type { TimelineFilters as TimelineFiltersType } from './types';
import { mergeLookups, useTimelineViewState } from './useTimelineViewState';

// Preview fetches a few more events than it renders so post-collapse
// shrinkage (sent/received dedup, cascade absorption) still leaves enough
// rows to fill the render window. Render 5 -> fetch 10 (issue #13177 KD3).
const PREVIEW_FETCH_HEADROOM = 5;

/**
 * Formats an entity type name for display.
 * Strips '_timeline' suffix, then delegates to formatFallback which
 * removes 'ahoy_' prefix and converts snake_case to Title Case.
 * e.g., "ahoy_contact" → "Contact"
 *       "ahoy_deal_pipeline_stage" → "Deal Pipeline Stage"
 */
function formatEntityTypeName(entityType: string): string {
  if (!entityType) {
    return '';
  }
  return formatFallback(entityType.replace(/_timeline$/, ''));
}

interface TimelineTabProps {
  entityId: string;
  orgId: string;
  entityType?: string;
  defaultFilters?: Partial<TimelineFiltersType>;
  /** When true, hides the header row (title + filters). Useful when the tab label already identifies the content. */
  hideHeader?: boolean;
  /** When true, removes the border and background wrapper around the event list. */
  noBorder?: boolean;
  /** When set, render at most this many timeline items and disable infinite scroll. */
  limit?: number;
  /** When true, omit the timeline filter control (independent of hideHeader). */
  hideFilters?: boolean;
  /**
   * Controlled-mode filter value. When provided together with
   * `onFiltersChange`, the parent owns the filter state — useful when
   * the filter trigger is rendered outside the timeline (e.g. in the
   * Full-page record view's tab strip). When omitted, the timeline
   * manages its own filter state internally.
   */
  filters?: TimelineFiltersType;
  /** Controlled-mode change handler — paired with `filters`. */
  onFiltersChange?: (filters: TimelineFiltersType) => void;
}

export function TimelineTab({
  entityId,
  orgId,
  entityType = '',
  defaultFilters = {},
  hideHeader = false,
  noBorder = false,
  limit,
  hideFilters = false,
  filters: filtersProp,
  onFiltersChange,
}: TimelineTabProps) {
  // Derive default event types from categories (Single Source of Truth)
  const defaultEventTypes = useMemo(() => {
    if (defaultFilters.eventTypes) {
      return defaultFilters.eventTypes;
    }

    // Default: show ALL event types (complete timeline)
    return ALL_EVENT_TYPES;
  }, [defaultFilters.eventTypes]);

  const user = useUser();
  const currentUserId = user?.id?.toString();

  // Shared with the filter control's "All" reset so it restores the same
  // related-activity default the state was seeded with (never `false`).
  const defaultIncludeAssociated = defaultFilters.includeAssociated ?? true;

  const [internalFilters, setInternalFilters] = useState<TimelineFiltersType>({
    eventTypes: defaultEventTypes,
    startDate: defaultFilters.startDate || null,
    endDate: defaultFilters.endDate || null,
    actorId: defaultFilters.actorId || null,
    includeAssociated: defaultIncludeAssociated,
  });

  // Controlled mode: use the parent's `filters` when provided; otherwise
  // fall back to internal state. The internal-state branch preserves
  // every existing call site that didn't pass `filters`/`onFiltersChange`.
  const filters = filtersProp ?? internalFilters;
  const setFilters = onFiltersChange ?? setInternalFilters;

  // When a render cap is set (preview), fetch that many + headroom instead of
  // the default 50. Undefined -> hook keeps its default 50 (full Activity tab).
  const requestLimit =
    limit != null ? limit + PREVIEW_FETCH_HEADROOM : undefined;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  } = useTimelineViewState({
    entityId,
    orgId,
    entityType,
    filters,
    payload: 'lean',
    limit: requestLimit,
  });

  // Flatten pages into single events array and transform to timeline items.
  // Event-type / actor / date filtering is handled by the TimelineFilters
  // system (via the `filters` passed to useTimelineViewState) — the overview
  // preview simply seeds an interaction-only `defaultFilters` set, the same way
  // the Emails/Files tabs do.
  const items = useMemo(() => {
    const flatEvents = collapseTimelineEvents(
      data?.pages.flatMap(page => page.events) ?? []
    );
    return collapseCascadeEvents(transformToTimelineItems(flatEvents));
  }, [data?.pages]);

  const visibleItems = limit != null ? items.slice(0, limit) : items;

  // The backend sets `related_threads_truncated` (response-level, identical
  // across pages) when a contact's related email-thread fan-out was capped to
  // the most-recently-active threads. `.some` is defensive — any page carrying
  // the flag surfaces the bottom-of-timeline notice.
  const relatedThreadsTruncated = useMemo(
    () => data?.pages.some(page => page.related_threads_truncated) ?? false,
    [data?.pages]
  );

  // Merge lookups from all pages
  const lookups = useMemo(() => {
    return data?.pages ? mergeLookups(data.pages) : emptyViewStateLookups;
  }, [data?.pages]);

  // Key for the fade-in animation — changes only when the newest event
  // changes (fresh data at the top), NOT when older pages are appended.
  // Including the item count here remounted TimelineEventList on every
  // infinite-scroll page load, which recreated the IntersectionObserver,
  // replayed the fade-in on the whole list, and re-armed the auto-load
  // effect — chain-loading the entity's ENTIRE history sequentially on
  // open (14 pages / ~44s of requests observed in production).
  const animationKey = useMemo(() => {
    const firstItem = visibleItems[0];
    return firstItem?.events[0]?.event_id ?? '';
  }, [visibleItems]);

  let content: ReactNode;

  if (isLoading) {
    content = (
      <TimelineSkeleton
        noBorder={noBorder}
        hideHeader={hideHeader}
        hideFilters={hideFilters}
        entityType={entityType}
      />
    );
  } else if (error) {
    content = (
      <QueryErrorState
        error={error}
        label="We couldn't load timeline events"
        onRetry={refetch}
        size='sm'
      />
    );
  } else if (noBorder) {
    content = (
      <div className='space-y-4'>
        {!hideHeader && (
          <div className='flex items-center justify-between'>
            <h3 className='text-heading-4'>
              {entityType
                ? `${formatEntityTypeName(entityType)} timeline`
                : 'Timeline'}
            </h3>
            {!hideFilters && (
              <TimelineFilters
                filters={filters}
                onFiltersChange={setFilters}
                defaultIncludeAssociated={defaultIncludeAssociated}
              />
            )}
          </div>
        )}
        <ErrorBoundary errorMessage="We couldn't render these timeline events">
          <ViewStateLookupsProvider
            lookups={lookups}
            currentUserId={currentUserId}
          >
            <div
              key={animationKey}
              className='animate-[fadeInGrow_400ms_ease-out_both]'
            >
              <TimelineEventList
                items={visibleItems}
                entityType={entityType}
                hasNextPage={limit != null ? false : (hasNextPage ?? false)}
                isFetchingNextPage={isFetchingNextPage}
                onLoadMore={fetchNextPage}
                hideEndOfTimelineMessage={limit != null}
                relatedThreadsTruncated={relatedThreadsTruncated}
              />
            </div>
          </ViewStateLookupsProvider>
        </ErrorBoundary>
      </div>
    );
  } else {
    content = (
      <SectionCard
        title={
          entityType
            ? `${formatEntityTypeName(entityType)} timeline`
            : 'Timeline'
        }
        actions={
          hideFilters ? undefined : (
            <TimelineFilters
              filters={filters}
              onFiltersChange={setFilters}
              defaultIncludeAssociated={defaultIncludeAssociated}
            />
          )
        }
        hideHeader={hideHeader}
        scrollable
      >
        <ErrorBoundary errorMessage="We couldn't render these timeline events">
          <ViewStateLookupsProvider
            lookups={lookups}
            currentUserId={currentUserId}
          >
            <div
              key={animationKey}
              className='animate-[fadeInGrow_400ms_ease-out_both]'
            >
              <TimelineEventList
                items={visibleItems}
                entityType={entityType}
                hasNextPage={limit != null ? false : (hasNextPage ?? false)}
                isFetchingNextPage={isFetchingNextPage}
                onLoadMore={fetchNextPage}
                hideEndOfTimelineMessage={limit != null}
                relatedThreadsTruncated={relatedThreadsTruncated}
              />
            </div>
          </ViewStateLookupsProvider>
        </ErrorBoundary>
      </SectionCard>
    );
  }

  return <div className='flex-1 overflow-hidden flex flex-col'>{content}</div>;
}

/** Skeleton that mirrors the real timeline layout (SectionCard + event rows) */
export function TimelineSkeleton({
  noBorder,
  hideHeader,
  hideFilters,
  entityType,
}: {
  noBorder: boolean;
  hideHeader: boolean;
  hideFilters: boolean;
  entityType: string;
}) {
  const title = entityType
    ? `${formatEntityTypeName(entityType)} timeline`
    : 'Timeline';

  const rows = [
    { textW: 'w-3/4', tsW: 'w-16' },
    { textW: 'w-2/3', tsW: 'w-14' },
    { textW: 'w-4/5', tsW: 'w-16' },
    { textW: 'w-1/2', tsW: 'w-12' },
    { textW: 'w-3/5', tsW: 'w-14' },
  ];

  const eventList = (
    <div className='space-y-0'>
      {rows.map((row, i) => (
        // eslint-disable-next-line react/no-array-index-key -- Skeleton loading rows; no item identity
        <div key={i} className='relative flex items-start gap-3 py-3'>
          {/* Connector line — matches TimelineEventList left-[13px] */}
          {i < rows.length - 1 && (
            <div className='absolute left-[13px] top-[39px] bottom-[-12px] w-px bg-border-secondary' />
          )}
          {/* Icon circle — matches EventIcon size-[26px] rounded-full bg-white */}
          <div className='size-[26px] rounded-full bg-bg-tertiary animate-pulse shrink-0' />
          {/* Content — matches TimelineEvent.Content flex layout */}
          <div className='flex-1 flex items-center justify-between gap-4 min-h-7'>
            <div
              className={`h-3.5 bg-bg-tertiary rounded animate-pulse ${row.textW}`}
            />
            <div
              className={`h-3 bg-bg-tertiary rounded animate-pulse shrink-0 ${row.tsW}`}
            />
          </div>
        </div>
      ))}
    </div>
  );

  if (noBorder) {
    return (
      <div className='space-y-4'>
        {!hideHeader && (
          <div className='flex items-center justify-between'>
            <h3 className='text-heading-4'>{title}</h3>
            {!hideFilters && (
              <div className='h-8 w-20 bg-bg-tertiary rounded animate-pulse' />
            )}
          </div>
        )}
        {eventList}
      </div>
    );
  }

  return (
    <div className='bg-primary border border-primary rounded-xl p-4 lg:p-6 flex-1'>
      {!hideHeader && (
        <div className='flex items-center justify-between pb-3 lg:pb-4 mb-3 lg:mb-4 min-h-[32px] lg:min-h-[36px] border-b border-gray-200 dark:border-gray-700'>
          <h2 className='text-sm lg:text-base 2xl:text-heading-3 font-semibold 2xl:font-medium text-primary'>
            {title}
          </h2>
          {!hideFilters && (
            <div className='h-8 w-20 bg-bg-tertiary rounded animate-pulse' />
          )}
        </div>
      )}
      {eventList}
    </div>
  );
}
